import React from 'react';

import { UserOutlined, WalletOutlined } from '@ant-design/icons';
import AccountAutocompleteItem from './AccountAutocompleteItem';
import { Account, Employee } from '~/types';
import { getCurrentBalance } from '~/utils/amounts';

type AccountOptionType = 'EMPLOYEE' | 'NUMBER';

export interface AccountData {
  title: string;
  number: string;
  type: AccountOptionType;
}

export class NumberAccountData implements AccountData {
  title: string;
  number: string;
  account: Account;
  type: AccountOptionType;

  constructor(account: Account) {
    this.title = (account.alias || 'Account').toLocaleLowerCase();
    this.number = account.number.toLocaleLowerCase();
    this.account = account;
    this.type = 'NUMBER';
  }
}

export class EmployeeAccountData implements AccountData {
  title: string;
  number: string;
  account: Account;
  employee: Employee;
  type: AccountOptionType;

  constructor(account: Account, employee: Employee) {
    this.title = `${employee.firstName} ${employee.lastName}`.toLocaleLowerCase();
    this.number = account.number.toLocaleLowerCase();
    this.account = account;
    this.employee = employee;
    this.type = 'EMPLOYEE';
  }
}

export interface AccountOption {
  value: string;
  label: JSX.Element;
  data: AccountData;
}

const EmployeeIcon = () => (
  <UserOutlined style={{ fontSize: '22px', verticalAlign: 'middle', opacity: 0.5 }} />
);

const WalletIcon = () => (
  <WalletOutlined style={{ fontSize: '22px', verticalAlign: 'middle', opacity: 0.5 }} />
);

const withPattern = (text: string, pattern: string): JSX.Element => {
  if (pattern) {
    const parts = text.toLocaleLowerCase().split(pattern);
    const patternPart = (i: number, offset: number) => (
      <span key={`p${i}`} className="search-pattern">
        {text.substring(offset, offset + pattern.length)}
      </span>
    );

    const elements: JSX.Element[] = [];
    let offset = 0;
    parts.forEach((part, i) => {
      if (part.length > 0) {
        elements.push(<span key={`t${i}`}>{text.substring(offset, offset + part.length)}</span>);
      }
      offset += part.length;
      if (i < parts.length - 1) {
        elements.push(patternPart(i, offset));
      }
      offset += pattern.length;
    });
    return <>{elements}</>;
  }
  return <>{text}</>;
};

export class NumberAccountOption implements AccountOption {
  value: string;
  label: JSX.Element;
  data: AccountData;

  constructor(data: NumberAccountData, pattern: string) {
    this.value = data.account.number;
    this.data = data;

    this.label = (
      <AccountAutocompleteItem
        title={withPattern(data.account.alias || 'Account', pattern)}
        subtitle={withPattern(data.account.number, pattern)}
        icon={<WalletIcon />}
        amount={getCurrentBalance(data.account)?.amount}
      />
    );
  }
}

export class EmployeeAccountOption implements AccountOption {
  value: string;
  label: JSX.Element;
  data: AccountData;

  constructor(data: EmployeeAccountData, pattern: string) {
    this.value = `${data.account.number}||||${data.employee.id}`;
    this.data = data;

    this.label = (
      <AccountAutocompleteItem
        title={withPattern(`${data.employee.firstName} ${data.employee.lastName}`, pattern)}
        subtitle={withPattern(data.account.number, pattern)}
        icon={<EmployeeIcon />}
        amount={getCurrentBalance(data.account)?.amount}
      />
    );
  }
}
